import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Shield, Edit, Check, X, Lock, Loader2, FileText, Camera, Smartphone, Link2, Copy, Gift, Users, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const Settings = () => {
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showSettingsPassword, setShowSettingsPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  // 2FA state
  const [twoFaStatus, setTwoFaStatus] = useState({ enabled: false, method: null });
  const [twoFaLoading, setTwoFaLoading] = useState(true);
  const [setupStep, setSetupStep] = useState(null); // null | 'choose' | 'totp-qr' | 'totp-verify' | 'email-confirm' | 'backup' | 'disable'
  const [totpData, setTotpData] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [twoFaActing, setTwoFaActing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const avatarInputRef = React.useRef(null);

  const getAvatarUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };
  const fullAvatarUrl = getAvatarUrl(profile?.avatar_url);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setProfile(data);
      } catch (error) {
        logger.error('Error fetching profile:', error);
      }
    };
    const fetch2faStatus = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/2fa/status`, { withCredentials: true });
        setTwoFaStatus(data);
      } catch (error) {
        logger.error('Error fetching 2FA status:', error);
      } finally {
        setTwoFaLoading(false);
      }
    };
    fetchProfile();
    fetch2faStatus();
    const fetchReferralInfo = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/referral/info`, { withCredentials: true });
        setReferralInfo(data);
      } catch (error) {
        logger.error('Error fetching referral info:', error);
      } finally {
        setReferralLoading(false);
      }
    };
    fetchReferralInfo();
  }, []);

  const startEditing = () => {
    setEditForm({
      name: profile?.name || '',
      license_number: profile?.license_number || '',
      license_issuer: profile?.license_issuer || ''
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditForm({});
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/auth/profile`, editForm, { withCredentials: true });
      setProfile(prev => ({ ...prev, ...data }));
      setEditing(false);
      toast.success('Profile updated');
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/avatar`, formData, { withCredentials: true });
      setProfile(prev => ({ ...prev, avatar_url: data.avatar_url }));
      toast.success('Profile picture updated');
      checkAuth();
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${BACKEND_URL}/api/auth/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setChangingPassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const displayProfile = profile || user;

  // 2FA actions
  const startTotpSetup = async () => {
    setTwoFaActing(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/2fa/setup-totp`, {}, { withCredentials: true });
      setTotpData(data);
      setSetupStep('totp-qr');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start TOTP setup');
    } finally {
      setTwoFaActing(false);
    }
  };

  const confirmTotpSetup = async () => {
    if (totpCode.length !== 6) return;
    setTwoFaActing(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/2fa/verify-totp-setup`, { code: totpCode }, { withCredentials: true });
      setBackupCodes(data.backup_codes);
      setTwoFaStatus({ enabled: true, method: 'totp' });
      setSetupStep('backup');
      toast.success('Authenticator app enabled!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setTwoFaActing(false);
    }
  };

  const enableEmail2fa = async () => {
    if (!emailPassword) { toast.error('Password required'); return; }
    setTwoFaActing(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/2fa/enable-email`, { password: emailPassword }, { withCredentials: true });
      setBackupCodes(data.backup_codes);
      setTwoFaStatus({ enabled: true, method: 'email' });
      setSetupStep('backup');
      setEmailPassword('');
      toast.success('Email 2FA enabled!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enable email 2FA');
    } finally {
      setTwoFaActing(false);
    }
  };

  const disable2fa = async () => {
    if (!disablePassword) { toast.error('Password required'); return; }
    setTwoFaActing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/2fa/disable`, { password: disablePassword }, { withCredentials: true });
      setTwoFaStatus({ enabled: false, method: null });
      setSetupStep(null);
      setDisablePassword('');
      toast.success('2FA disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setTwoFaActing(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">Settings</h1>
        <p className="text-[#5C5F5C] mt-1">Manage your account settings</p>
      </div>

      {/* Profile Card */}
      <Card className="border-[#E5E4E0] bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
            <User className="w-5 h-5 text-[#4B6651]" />
            Profile Information
          </CardTitle>
          {!editing && (
            <Button
              variant="outline"
              size="sm"
              onClick={startEditing}
              className="border-[#E5E4E0] text-[#5C5F5C] hover:text-[#4B6651]"
              data-testid="edit-profile-btn"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <div className="space-y-4" data-testid="profile-edit-form">
              <div className="space-y-2">
                <Label className="text-xs text-[#5C5F5C] font-medium">Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                  data-testid="edit-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-[#5C5F5C] font-medium">License Number</Label>
                  <Input
                    value={editForm.license_number}
                    onChange={(e) => setEditForm({ ...editForm, license_number: e.target.value })}
                    className="border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="edit-license-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-[#5C5F5C] font-medium">Issuing Entity</Label>
                  <Input
                    value={editForm.license_issuer}
                    onChange={(e) => setEditForm({ ...editForm, license_issuer: e.target.value })}
                    className="border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="edit-license-issuer"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  onClick={saveProfile}
                  disabled={saving}
                  className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
                  data-testid="save-profile-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={cancelEditing}
                  className="border-[#E5E4E0]"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 p-4 bg-[#F8F7F4] rounded-lg">
                <div className="relative group">
                  <div className="w-16 h-16 bg-[#4B6651] rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {fullAvatarUrl ? (
                      <img src={fullAvatarUrl} alt="Avatar" className="w-full h-full object-cover" data-testid="profile-avatar" />
                    ) : (
                      <span className="text-2xl font-semibold text-white" data-testid="profile-initial">
                        {displayProfile?.name?.charAt(0) || 'U'}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    data-testid="upload-avatar-btn"
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="avatar-file-input"
                  />
                </div>
                <div>
                  <p className="text-lg font-semibold text-[#2A2C2A]" data-testid="profile-name">{displayProfile?.name}</p>
                  <p className="text-sm text-[#5C5F5C]">{displayProfile?.role === 'admin' ? 'Administrator' : 'Veterinarian'}</p>
                  <p className="text-xs text-[#5C5F5C] mt-1">Hover photo to change</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-[#F8F7F4] rounded-lg">
                  <Mail className="w-5 h-5 text-[#5C5F5C] flex-shrink-0" />
                  <div>
                    <Label className="text-xs text-[#5C5F5C] uppercase tracking-wider">Email</Label>
                    <p className="text-[#2A2C2A]" data-testid="profile-email">{displayProfile?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-[#F8F7F4] rounded-lg">
                  <Shield className="w-5 h-5 text-[#5C5F5C] flex-shrink-0" />
                  <div>
                    <Label className="text-xs text-[#5C5F5C] uppercase tracking-wider">Role</Label>
                    <p className="text-[#2A2C2A] capitalize">{displayProfile?.role}</p>
                  </div>
                </div>

                {displayProfile?.license_number && (
                  <div className="flex items-center gap-3 p-3 bg-[#F8F7F4] rounded-lg">
                    <FileText className="w-5 h-5 text-[#5C5F5C] flex-shrink-0" />
                    <div>
                      <Label className="text-xs text-[#5C5F5C] uppercase tracking-wider">License</Label>
                      <p className="text-[#2A2C2A]" data-testid="profile-license">
                        {displayProfile.license_number}
                        {displayProfile.license_issuer && (
                          <span className="text-[#5C5F5C]"> — {displayProfile.license_issuer}</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Password Card */}
      <Card className="border-[#E5E4E0] bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#4B6651]" />
            Security
          </CardTitle>
          {!changingPassword && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setChangingPassword(true)}
              className="border-[#E5E4E0] text-[#5C5F5C] hover:text-[#4B6651]"
              data-testid="change-password-btn"
            >
              Change Password
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {changingPassword ? (
            <form onSubmit={changePassword} className="space-y-4" data-testid="password-form">
              <div className="space-y-2">
                <Label className="text-xs text-[#5C5F5C] font-medium">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showSettingsPassword ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    required
                    className="border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651] pr-10"
                    data-testid="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSettingsPassword(!showSettingsPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5F5C] hover:text-[#2A2C2A]"
                    data-testid="settings-toggle-password"
                  >
                    {showSettingsPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-[#5C5F5C] font-medium">New Password</Label>
                  <Input
                    type={showSettingsPassword ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    required
                    minLength={6}
                    className="border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-[#5C5F5C] font-medium">Confirm New Password</Label>
                  <Input
                    type={showSettingsPassword ? 'text' : 'password'}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    required
                    minLength={6}
                    className="border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="confirm-password"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={savingPassword}
                  className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
                  data-testid="save-password-btn"
                >
                  {savingPassword ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Update Password
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => { setChangingPassword(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}
                  className="border-[#E5E4E0]"
                  data-testid="cancel-password-btn"
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-[#5C5F5C]">
              Password last changed: Not tracked. Click "Change Password" to update your password.
            </p>
          )}
        </CardContent>
      </Card>

      {/* About Card */}
      {/* Two-Factor Authentication Card */}
      <Card className="border-[#E5E4E0] bg-white" data-testid="2fa-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#4B6651]" />
              Two-Factor Authentication
            </CardTitle>
            {!twoFaLoading && (
              <span className={`text-xs px-2 py-1 rounded ${twoFaStatus.enabled ? 'bg-[#4B6651]/10 text-[#4B6651]' : 'bg-[#F8F7F4] text-[#5C5F5C]'}`} data-testid="2fa-status-badge">
                {twoFaStatus.enabled ? `Enabled (${twoFaStatus.method === 'totp' ? 'Authenticator' : 'Email'})` : 'Disabled'}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {twoFaLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#4B6651]" /></div>
          ) : twoFaStatus.enabled && !setupStep ? (
            <div className="space-y-3">
              <p className="text-sm text-[#5C5F5C]">
                Your account is protected with {twoFaStatus.method === 'totp' ? 'an authenticator app' : 'email verification codes'}.
              </p>
              {setupStep !== 'disable' ? (
                <Button variant="outline" size="sm" onClick={() => setSetupStep('disable')} className="border-[#E5E4E0] text-[#5C5F5C]" data-testid="disable-2fa-btn">
                  Disable 2FA
                </Button>
              ) : null}
              {setupStep === 'disable' && (
                <div className="p-4 bg-[#F8F7F4] rounded-lg space-y-3">
                  <p className="text-sm text-[#5C5F5C]">Enter your password to disable 2FA:</p>
                  <Input type="password" placeholder="Your password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} className="border-[#E5E4E0] bg-white h-9" data-testid="disable-2fa-password" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={disable2fa} disabled={twoFaActing} className="bg-[#D94F4F] hover:bg-[#C04040] text-white" data-testid="confirm-disable-2fa-btn">
                      {twoFaActing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Disable'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSetupStep(null); setDisablePassword(''); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ) : setupStep === 'backup' ? (
            <div className="space-y-3" data-testid="backup-codes-section">
              <p className="text-sm font-medium text-[#2A2C2A]">Save your backup codes</p>
              <p className="text-xs text-[#5C5F5C]">Store these codes in a safe place. Each code can only be used once if you lose access to your 2FA method.</p>
              <div className="grid grid-cols-2 gap-2 p-4 bg-[#F8F7F4] rounded-lg font-mono text-sm">
                {backupCodes.map((code, i) => (
                  <span key={`backup-${code}`} className="text-[#2A2C2A]" data-testid={`backup-code-${i}`}>{code}</span>
                ))}
              </div>
              <Button size="sm" onClick={() => { setSetupStep(null); setTotpCode(''); setTotpData(null); }} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="done-2fa-btn">
                Done
              </Button>
            </div>
          ) : setupStep === 'totp-qr' ? (
            <div className="space-y-4" data-testid="totp-setup-section">
              <p className="text-sm text-[#5C5F5C]">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):</p>
              <div className="flex justify-center">
                {totpData?.qr_code && <img src={totpData.qr_code} alt="QR Code" className="w-48 h-48" data-testid="totp-qr-code" />}
              </div>
              <p className="text-xs text-center text-[#5C5F5C]">Or enter this key manually: <code className="bg-[#F8F7F4] px-1 py-0.5 rounded text-[#2A2C2A]">{totpData?.secret}</code></p>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2A2C2A]">Enter the 6-digit code from your app:</Label>
                <Input type="text" inputMode="numeric" placeholder="000000" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="text-center text-xl tracking-[0.2em] font-mono border-[#E5E4E0] bg-white h-12" data-testid="totp-setup-code-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={confirmTotpSetup} disabled={twoFaActing || totpCode.length !== 6} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="confirm-totp-btn">
                  {twoFaActing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Verify & Enable
                </Button>
                <Button variant="ghost" onClick={() => { setSetupStep(null); setTotpData(null); setTotpCode(''); }}>Cancel</Button>
              </div>
            </div>
          ) : setupStep === 'email-confirm' ? (
            <div className="space-y-3" data-testid="email-2fa-setup">
              <p className="text-sm text-[#5C5F5C]">A verification code will be sent to your email each time you log in. Enter your password to confirm:</p>
              <Input type="password" placeholder="Your password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} className="border-[#E5E4E0] bg-white h-10" data-testid="email-2fa-password" />
              <div className="flex gap-2">
                <Button onClick={enableEmail2fa} disabled={twoFaActing || !emailPassword} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="confirm-email-2fa-btn">
                  {twoFaActing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Enable Email 2FA
                </Button>
                <Button variant="ghost" onClick={() => { setSetupStep(null); setEmailPassword(''); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#5C5F5C]">Add an extra layer of security to your account. Choose your preferred method:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={startTotpSetup} disabled={twoFaActing} className="p-4 border border-[#E5E4E0] rounded-lg hover:border-[#4B6651] hover:bg-[#4B6651]/5 transition-all text-left" data-testid="setup-totp-btn">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-4 h-4 text-[#4B6651]" />
                    <span className="text-sm font-medium text-[#2A2C2A]">Authenticator App</span>
                  </div>
                  <p className="text-xs text-[#5C5F5C]">Use Google Authenticator, Authy, or similar app</p>
                </button>
                <button onClick={() => setSetupStep('email-confirm')} className="p-4 border border-[#E5E4E0] rounded-lg hover:border-[#4B6651] hover:bg-[#4B6651]/5 transition-all text-left" data-testid="setup-email-2fa-btn">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-[#4B6651]" />
                    <span className="text-sm font-medium text-[#2A2C2A]">Email Code</span>
                  </div>
                  <p className="text-xs text-[#5C5F5C]">Receive a code via email on each login</p>
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referral Card */}
      <Card className="border-[#E5E4E0] bg-white" data-testid="referral-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#4B6651]" />
              Referral Program
            </CardTitle>
            {referralInfo && (
              <span className="text-xs px-2 py-1 bg-[#4B6651]/10 text-[#4B6651] rounded-full font-medium">
                {referralInfo.completed_referrals} completed
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {referralLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-[#4B6651]" />
            </div>
          ) : referralInfo ? (
            <>
              <div className="p-4 bg-[#4B6651]/5 rounded-lg border border-[#4B6651]/20">
                <p className="text-xs text-[#5C5F5C] mb-2 font-medium uppercase tracking-wide">Your Referral Link</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-2.5 bg-white rounded border border-[#E5E4E0] text-sm text-[#2A2C2A] font-mono truncate" data-testid="referral-link">
                    {window.location.origin}/register?ref={referralInfo.referral_code}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`flex-shrink-0 border-[#4B6651] ${copied ? 'bg-[#4B6651] text-white' : 'text-[#4B6651] hover:bg-[#4B6651]/10'}`}
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/register?ref=${referralInfo.referral_code}`);
                      setCopied(true);
                      toast.success('Referral link copied!');
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    data-testid="copy-referral-link"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-[#F8F7F4] rounded-lg">
                  <p className="text-xl font-semibold text-[#2A2C2A]" data-testid="total-referrals">{referralInfo.total_referrals}</p>
                  <p className="text-xs text-[#5C5F5C]">Total</p>
                </div>
                <div className="text-center p-3 bg-[#F8F7F4] rounded-lg">
                  <p className="text-xl font-semibold text-[#4B6651]" data-testid="completed-referrals">{referralInfo.completed_referrals}</p>
                  <p className="text-xs text-[#5C5F5C]">Completed</p>
                </div>
                <div className="text-center p-3 bg-[#F8F7F4] rounded-lg">
                  <p className="text-xl font-semibold text-[#C06144]" data-testid="pending-referrals">{referralInfo.pending_referrals}</p>
                  <p className="text-xs text-[#5C5F5C]">Pending</p>
                </div>
              </div>
              <p className="text-sm text-[#5C5F5C]">
                <strong className="text-[#2A2C2A]">Reward:</strong> {referralInfo.reward}
              </p>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* About Card */}
      <Card className="border-[#E5E4E0] bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-['Work_Sans']">About VetNeuroHelp</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[#5C5F5C]">
            VetNeuroHelp is an AI-powered veterinary neurology assistant designed to help 
            practicing veterinarians with neurological lesion localization, differential 
            diagnoses, and diagnostic planning for dogs and cats.
          </p>
          <div className="p-4 bg-[#F8F7F4] rounded-lg">
            <p className="text-sm text-[#5C5F5C]">
              <strong className="text-[#2A2C2A]">Data Sources:</strong> Information is curated 
              from publicly available information provided by 53 veterinary programs worldwide including 33 US, 12 European, 5 Canadian, and 3 Asia-Pacific institutions.
            </p>
          </div>
          <div className="p-4 bg-[#4B6651]/10 rounded-lg">
            <p className="text-sm text-[#4B6651]">
              <strong>AI Model:</strong> GPT-5.2 powers the diagnostic analysis and 
              rule-out generation capabilities.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
